import { Component, OnInit, OnDestroy } from '@angular/core';
import * as Editor from '../../../../assets/ckeditor/build/ckeditor';
import { AdminService } from '../../../shared/services/admin.service';
import { MessageService } from 'primeng/api';

interface DiagnosisCategory {
  diagnosis_id: number;
  diagnosis_title: string;
}

interface PracticeCase {
  case_id: number;
  case_title: string;
  case_content: string;
  diagnosis_id: number;
  diagnosis_title?: string;
}

// Custom Upload Adapter for CKEditor
class CustomUploadAdapter {
  private loader: any;
  private adminService: AdminService;
  private messageService: MessageService;

  constructor(loader: any, adminService: AdminService, messageService: MessageService) {
    this.loader = loader;
    this.adminService = adminService;
    this.messageService = messageService;
  }

  upload() {
    return this.loader.file.then((file: File) => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('media', file);

        this.adminService.uploadPracticeCaseMedia(formData).subscribe({
          next: (response: any) => {
            if (response.success && response.data && response.data.url) {
              resolve({
                default: response.data.url
              });
            } else {
              reject(response.message || 'Upload failed');
            }
          },
          error: (error) => {
            console.error('Media upload error:', error);
            this.messageService.add({
              severity: 'error', 
              summary: 'Upload Error', 
              detail: 'Failed to upload media file'
            });
            reject('Upload failed');
          }
        });
      });
    });
  }

  abort() {
    // Handle upload abort if needed
  }
}

@Component({
  selector: 'app-admin-practise-case',
  templateUrl: './admin-practise-case.component.html',
  styleUrls: ['./admin-practise-case.component.scss']
})
export class AdminPractiseCaseComponent implements OnInit, OnDestroy {

  // Data arrays
  practiceCases: PracticeCase[] = [];
  diagnosisCategories: DiagnosisCategory[] = [];
  
  // Loading states
  loading: boolean = false;
  modalLoader: boolean = false;
  
  // Modal states
  showCaseModal: boolean = false;
  showDiagnosisModal: boolean = false;
  showDeleteConfirmModal: boolean = false;
  showViewContentModal: boolean = false;
  
  // Modal types
  modalType: 'add' | 'edit' = 'add';
  deleteType: 'case' | 'diagnosis' = 'case';
  
  // Form data
  caseForm = {
    case_id: null,
    case_title: '',
    case_content: '',
    diagnosis_id: null
  };
  
  diagnosisForm = {
    diagnosis_id: null,
    diagnosis_title: ''
  };
  
  // Delete confirmation
  itemToDelete: any = null;
  
  // Selected case for viewing content
  selectedCase: PracticeCase | null = null;
  selectedCaseIndex: number = 0;

  // CKEditor instance
  ckEditorNotebook: any;

  constructor(
    private adminService: AdminService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    // Destroy CKEditor instance when component is destroyed
    if (this.ckEditorNotebook && this.ckEditorNotebook.state !== 'destroyed') {
      this.ckEditorNotebook.destroy();
    }
  }

  loadData(): void {
    this.loading = true;
    this.loadPracticeCases();
    this.loadDiagnosisCategories();
  }

  loadPracticeCases(): void {
    this.loading = true;
    this.adminService.getPracticeCases().subscribe({
      next: (response: any) => {
        if (response.success) {
          this.practiceCases = response.data || [];
          this.mapDiagnosisTitles();
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading practice cases:', error);
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'Failed to load practice cases'});
        this.loading = false;
      }
    });
  }

  loadDiagnosisCategories(): void {
    this.adminService.getDiagnosisCategories().subscribe({
      next: (response: any) => {
        if (response.success) {
          this.diagnosisCategories = response.data || [];
          this.mapDiagnosisTitles();
        }
      },
      error: (error) => {
        console.error('Error loading diagnosis categories:', error);
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'Failed to load diagnosis categories'});
      }
    });
  }

  mapDiagnosisTitles(): void {
    this.practiceCases.forEach(practiceCase => {
      const diagnosis = this.diagnosisCategories.find(d => d.diagnosis_id === practiceCase.diagnosis_id);
      practiceCase.diagnosis_title = diagnosis ? diagnosis.diagnosis_title : 'Unknown';
    });
  }

  // Practice Case Methods
  openCaseModal(type: 'add' | 'edit', practiceCase?: PracticeCase): void {
    this.modalType = type;
    this.showCaseModal = true;
    
    if (type === 'edit' && practiceCase) {
      this.caseForm = {
        case_id: practiceCase.case_id,
        case_title: practiceCase.case_title,
        case_content: practiceCase.case_content,
        diagnosis_id: practiceCase.diagnosis_id
      };
      // Initialize CKEditor with existing content
      setTimeout(() => this.initializeCKEditor(practiceCase.case_content), 100);
    } else {
      this.resetCaseForm();
      // Initialize CKEditor for new case
      setTimeout(() => this.initializeCKEditor(), 100);
    }
  }

  savePracticeCase(): void {
    // Get content from CKEditor
    const caseContent = this.getCKEditorContent();
    
    if (!this.caseForm.case_title.trim() || !caseContent || !this.caseForm.diagnosis_id) {
      this.messageService.add({severity: 'warn', summary: 'Warning', detail: 'Please fill all required fields'});
      return;
    }

    // Update form with CKEditor content
    this.caseForm.case_content = caseContent;

    this.modalLoader = true;
    
    const apiCall = this.modalType === 'add' 
      ? this.adminService.addPracticeCase(this.caseForm)
      : this.adminService.editPracticeCase(this.caseForm);

    apiCall.subscribe({
      next: (response: any) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success', 
            summary: 'Success', 
            detail: `Practice case ${this.modalType === 'add' ? 'added' : 'updated'} successfully`
          });
          this.showCaseModal = false;
          this.loadPracticeCases();
        } else {
          this.messageService.add({severity: 'error', summary: 'Error', detail: response.message || 'Operation failed'});
        }
        this.modalLoader = false;
      },
      error: (error) => {
        console.error('Error saving practice case:', error);
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'Failed to save practice case'});
        this.modalLoader = false;
      }
    });
  }

  deletePracticeCase(practiceCase: PracticeCase): void {
    this.deleteType = 'case';
    this.itemToDelete = practiceCase;
    this.showDeleteConfirmModal = true;
  }

  // Diagnosis Category Methods
  openDiagnosisModal(type: 'add' | 'edit', diagnosis?: DiagnosisCategory): void {
    this.modalType = type;
    this.showDiagnosisModal = true;
    
    if (type === 'edit' && diagnosis) {
      this.diagnosisForm = {
        diagnosis_id: diagnosis.diagnosis_id,
        diagnosis_title: diagnosis.diagnosis_title
      };
    } else {
      this.resetDiagnosisForm();
    }
  }

  saveDiagnosisCategory(): void {
    if (!this.diagnosisForm.diagnosis_title.trim()) {
      this.messageService.add({severity: 'warn', summary: 'Warning', detail: 'Please enter diagnosis title'});
      return;
    }

    this.modalLoader = true;
    
    const apiCall = this.modalType === 'add' 
      ? this.adminService.addDiagnosisCategory(this.diagnosisForm)
      : this.adminService.editDiagnosisCategory(this.diagnosisForm);

    apiCall.subscribe({
      next: (response: any) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success', 
            summary: 'Success', 
            detail: `Diagnosis category ${this.modalType === 'add' ? 'added' : 'updated'} successfully`
          });
          this.showDiagnosisModal = false;
          this.loadDiagnosisCategories();
        } else {
          this.messageService.add({severity: 'error', summary: 'Error', detail: response.message || 'Operation failed'});
        }
        this.modalLoader = false;
      },
      error: (error) => {
        console.error('Error saving diagnosis category:', error);
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'Failed to save diagnosis category'});
        this.modalLoader = false;
      }
    });
  }

  deleteDiagnosisCategory(diagnosis: DiagnosisCategory): void {
    this.deleteType = 'diagnosis';
    this.itemToDelete = diagnosis;
    this.showDeleteConfirmModal = true;
  }

  // Delete Confirmation
  confirmDelete(): void {
    if (!this.itemToDelete) return;

    this.modalLoader = true;
    
    const apiCall = this.deleteType === 'case' 
      ? this.adminService.deletePracticeCase({case_id: this.itemToDelete.case_id})
      : this.adminService.deleteDiagnosisCategory({diagnosis_id: this.itemToDelete.diagnosis_id});

    apiCall.subscribe({
      next: (response: any) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success', 
            summary: 'Success', 
            detail: `${this.deleteType === 'case' ? 'Practice case' : 'Diagnosis category'} deleted successfully`
          });
          this.showDeleteConfirmModal = false;
          if (this.deleteType === 'case') {
            this.loadPracticeCases();
          } else {
            this.loadDiagnosisCategories();
          }
        } else {
          this.messageService.add({severity: 'error', summary: 'Error', detail: response.message || 'Delete failed'});
        }
        this.modalLoader = false;
      },
      error: (error) => {
        console.error('Error deleting item:', error);
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'Failed to delete item'});
        this.modalLoader = false;
      }
    });
  }

  // Utility Methods
  resetCaseForm(): void {
    this.caseForm = {
      case_id: null,
      case_title: '',
      case_content: '',
      diagnosis_id: null
    };
  }

  resetDiagnosisForm(): void {
    this.diagnosisForm = {
      diagnosis_id: null,
      diagnosis_title: ''
    };
  }

  closeCaseModal(): void {
    this.showCaseModal = false;
    this.resetCaseForm();
    
    // Destroy CKEditor instance
    if (this.ckEditorNotebook && this.ckEditorNotebook.state !== 'destroyed') {
      this.ckEditorNotebook.destroy();
    }
  }

  closeDiagnosisModal(): void {
    this.showDiagnosisModal = false;
    this.resetDiagnosisForm();
  }

  closeDeleteModal(): void {
    this.showDeleteConfirmModal = false;
    this.itemToDelete = null;
  }

  // CKEditor Methods
  initializeCKEditor(content: string = ''): void {
    // Destroy existing editor if it exists
    if (this.ckEditorNotebook && this.ckEditorNotebook.state !== 'destroyed') {
      this.ckEditorNotebook.destroy();
    }

    // Initialize new editor after a short delay to ensure DOM is ready
    setTimeout(() => {
      const editorElement = document.querySelector("#ckEditorNotebook");
      if (editorElement) {
        Editor.create(editorElement, {
          // Enhanced toolbar with image and media options
          toolbar: {
            items: [
              'heading',
              '|',
              'fontFamily',
              'fontSize',
              '|',
              'bold',
              'italic',
              'underline',
              'strikethrough',
              '|',
              'fontColor',
              'fontBackgroundColor',
              '|',
              'bulletedList',
              'numberedList',
              'alignment',
              '|',
              'link',
              'imageUpload',
              'mediaEmbed',
              'code',
              '|',
              'insertTable',
              '|',
              'undo',
              'redo'
            ]
          },
          // Image upload configuration
          image: {
            toolbar: [
              'imageTextAlternative',
              'imageStyle:inline',
              'imageStyle:block',
              'imageStyle:side',
              '|',
              'toggleImageCaption',
              'imageResize'
            ],
            resizeOptions: [
              {
                name: 'resizeImage:original',
                label: 'Original',
                value: null
              },
              {
                name: 'resizeImage:50',
                label: '50%',
                value: '50'
              },
              {
                name: 'resizeImage:75',
                label: '75%',
                value: '75'
              }
            ]
          },
          // Table configuration
          table: {
            contentToolbar: [
              'tableColumn',
              'tableRow',
              'mergeTableCells'
            ]
          },
          // Media embed configuration
          mediaEmbed: {
            previewsInData: true
          },
          // Font size configuration
          fontSize: {
            options: [
              9,
              11,
              13,
              'default',
              17,
              19,
              21
            ]
          },
          // Heading configuration
          heading: {
            options: [
              { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
              { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
              { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
              { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
            ]
          }
        }).then((editor: any) => {
          this.ckEditorNotebook = editor;
          
          // Set up custom upload adapter
          editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
            return new CustomUploadAdapter(loader, this.adminService, this.messageService);
          };
          
          // Set content if provided
          if (content) {
            editor.setData(content);
          }
          
          // Style the editor container
          setTimeout(() => {
            const editorContainer = document.getElementById('ckEditorContainer');
            if (editorContainer) {
              editorContainer.style.border = '1px solid #ccced1';
              editorContainer.style.borderRadius = '4px';
            }
          }, 100);
        }).catch((error: any) => {
          console.error('Error initializing CKEditor:', error);
          this.messageService.add({
            severity: 'error', 
            summary: 'Editor Error', 
            detail: 'Failed to initialize the editor'
          });
        });
      }
    }, 100);
  }

  getCKEditorContent(): string {
    return this.ckEditorNotebook ? this.ckEditorNotebook.getData() : '';
  }

  // View Content Methods
  viewCaseContent(practiceCase: PracticeCase): void {
    this.selectedCase = practiceCase;
    // Find the index of the selected case in the array
    this.selectedCaseIndex = this.practiceCases.findIndex(pc => pc.case_id === practiceCase.case_id) + 1;
    this.showViewContentModal = true;
  }

  closeViewContentModal(): void {
    this.showViewContentModal = false;
    this.selectedCase = null;
    this.selectedCaseIndex = 0;
  }

}
